/* eslint-disable array-callback-return */
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import "../Reconciliation/style.css";
import { v4 as uuidV4 } from "uuid";
import * as XLSX from "xlsx";
import React, { useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { colors } from "../brand";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UndoIcon from "@mui/icons-material/Undo";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import moment from "moment";
import Skeleton from "@mui/material/Skeleton";
import { message } from "antd";
import { useSelector } from "react-redux";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

import { MutatingDots } from "react-loader-spinner";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ErrorIcon from "@mui/icons-material/Error";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { Modal } from "antd";
import { Helmet } from "react-helmet-async";
import API from "../Api/api";
import toast from "react-hot-toast";
import secureLocalStorage from "react-secure-storage";
import { uniqueObjects } from "../utils/uniqueObjects";

const getTransactionTypeStyles = (source = "") => {
  const normalized = String(source).toLowerCase();

  if (normalized.includes("debit")) {
    return {
      background: "linear-gradient(90deg, #fff1f0 0%, #fff7f6 100%)",
      borderLeft: "4px solid #ff7875",
    };
  }

  if (normalized.includes("credit")) {
    return {
      background: "linear-gradient(90deg, #f6ffed 0%, #fcfff8 100%)",
      borderLeft: "4px solid #73d13d",
    };
  }

  return {
    background: "#fafafa",
    borderLeft: "4px solid #d9d9d9",
  };
};

const getAmountGroupBadgeStyle = () => ({
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  background: "linear-gradient(90deg, #e6f4ff 0%, #f0f5ff 100%)",
  color: "#1d39c4",
  border: "1px solid #91caff",
});

// Reconciles a single account statement against itself: debit entries are
// matched against credit entries of the same amount (offsetting / reversal
// entries), and whatever amount is left over on either side is outstanding.
// One line of the getting-started checklist
const StepLine = ({ done, n, children }) => (
  <Stack direction="row" spacing={1.2} alignItems="flex-start">
    {done ? (
      <CheckCircleIcon sx={{ color: colors.secondary, fontSize: 22 }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ color: "#cbd5e1", fontSize: 22 }} />
    )}
    <Typography sx={{ fontSize: 14.5, color: done ? colors.textSecondary : colors.textPrimary }}>
      <strong>{n}.</strong> {children}
    </Typography>
  </Stack>
);

export default function SingleReconciliation() {
  const [open, setOpen] = useState(false);
  const [removedItemsOpen, setRemovedItemsOpen] = useState(false);
  const [autoRemovedItems, setAutoRemovedItems] = useState([]);
  const [selectedAutoRemovedRows, setSelectedAutoRemovedRows] = useState([]);

  const showModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };


  const sortedAutoRemovedItems = React.useMemo(() => {
    return [...autoRemovedItems].sort((a, b) => {
      const amountDiff = Number(a.amount || 0) - Number(b.amount || 0);
      if (amountDiff !== 0) return amountDiff;
      return String(a.source || "").localeCompare(String(b.source || ""));
    });
  }, [autoRemovedItems]);

  const toggleAutoRemovedSelection = (id) => {
    setSelectedAutoRemovedRows((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  const restoreSelectedAutoRemovedItems = () => {
    if (selectedAutoRemovedRows.length === 0) {
      message.info("Please select transaction(s) to post back");
      return;
    }

    const selectedItems = autoRemovedItems.filter((item) =>
      selectedAutoRemovedRows.includes(item.id),
    );

    if (selectedItems.length === 0) {
      message.info("No selected transactions found");
      return;
    }

    const restoredCredit = [];
    const restoredDebit = [];

    selectedItems.forEach((item) => {
      const baseRow = {
        id: uuidV4(),
        value_date: item.source_date || "",
        description: item.source_description || "",
        ids: Number(item.amount || 0),
      };

      if (item.source === "Credit") {
        restoredCredit.push({
          ...baseRow,
          credit_amt: Number(item.amount || 0),
        });
      }

      if (item.source === "Debit") {
        restoredDebit.push({
          ...baseRow,
          debit_amt: Number(item.amount || 0),
        });
      }
    });

    if (restoredCredit.length > 0) {
      setOutCr((prev) => [...prev, ...restoredCredit]);
    }

    if (restoredDebit.length > 0) {
      setOutDr((prev) => [...prev, ...restoredDebit]);
    }

    setAutoRemovedItems((prev) =>
      prev.filter((item) => !selectedAutoRemovedRows.includes(item.id)),
    );
    setSelectedAutoRemovedRows([]);
    message.success("Selected transaction(s) posted back to outstanding table");
  };

  const closeRemovedItemsModal = () => {
    setRemovedItemsOpen(false);
    setSelectedAutoRemovedRows([]);
  };

  const fileInputRef = useRef(null);
  const user = JSON.parse(secureLocalStorage.getItem("profile"));
  const [statementData, setStatementData] = React.useState([]);

  // outstanding debit and credit for this statement
  const [outDr, setOutDr] = React.useState([]);
  const [outCr, setOutCr] = React.useState([]);

  const [trigger, setTrigger] = React.useState(false);
  const [location, setLocation] = React.useState("");
  const [outCrPrev1, setOutCrPrev] = React.useState([]);
  const [outDrPrev1, setOutDrPrev] = React.useState([]);
  const [loader, setLoader] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleChangeLocation = (event) => {
    setLocation(event.target.value);
  };
  const [data, setData] = React.useState({
    date: "",
  });

  const locations = useSelector((state) => state.location);

  function formatData(value_date, description, debit_amt, credit_amt) {
    return {
      value_date,
      description,
      debit_amt,
      credit_amt,
    };
  }

  // Cleaning the uploaded statement is expensive on large files, so it only
  // re-runs when a new file is uploaded, not on every keystroke or checkbox.
  const { statementFinalData, dr_final, cr_final } = React.useMemo(() => {
    const statementFinalData = statementData?.map((i) => {
      return formatData(
        i.value_date,
        i.description,
        Math.abs(Number(i.debit_amt)),
        Math.abs(Number(i.credit_amt)),
      );
    });

    function debitData(value_date, description, debit_amt) {
      return {
        value_date,
        description,
        debit_amt,
      };
    }
    function creditData(value_date, description, credit_amt) {
      return {
        value_date,
        description,
        credit_amt,
      };
    }

    // ! Extracting debit and credit data from the single statement
    const dr = statementFinalData?.map((debit) => {
      return debitData(debit.value_date, debit.description, debit.debit_amt);
    });
    const cr = statementFinalData?.map((credit) => {
      return creditData(credit.value_date, credit.description, credit.credit_amt);
    });

    let dr_final_1 = [];
    let cr_final_1 = [];
    // ! we filter out all zeros
    dr.filter((d) => {
      if (d.debit_amt > 0) {
        return dr_final_1.push(d);
      }
    });
    cr.filter((d) => {
      if (d.credit_amt > 0) {
        return cr_final_1.push(d);
      }
    });

    const wordsToRemove = [
      "InterBranch Debit INTERCO ENTRY",
      "Inter-Branch Debit INTERCO ENTRY",
      "InterBranch Credit INTERCO ENTRY",
      "Inter-Branch Credit INTERCO ENTRY",
    ];

    const dr_final = dr_final_1.filter((outstanding) => {
      for (const word of wordsToRemove) {
        if (outstanding.description.includes(word)) {
          return false;
        }
      }
      return true;
    });
    const cr_final = cr_final_1.filter((outstanding) => {
      for (const word of wordsToRemove) {
        if (outstanding.description.includes(word)) {
          return false;
        }
      }
      return true;
    });

    // ids used to match debits against credits by amount
    cr_final.forEach((el) => Object.assign(el, { ids: el.credit_amt }));
    dr_final.forEach((el) => Object.assign(el, { ids: el.debit_amt }));

    return { statementFinalData, dr_final, cr_final };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementData]);

  function getMatchedItems(
    array1,
    array2,
    amountKey,
    sourceLabel,
    matchedWithLabel,
  ) {
    const matched = [];

    // Unused indexes of array2 per amount, in order, so each lookup is direct
    // instead of scanning array2 for every row of array1
    const unusedIndexesByAmount = new Map();
    array2.forEach((item2, index) => {
      const amount = Number(item2.ids);
      if (!unusedIndexesByAmount.has(amount)) {
        unusedIndexesByAmount.set(amount, []);
      }
      unusedIndexesByAmount.get(amount).push(index);
    });

    array1.forEach((item1) => {
      const candidates = unusedIndexesByAmount.get(Number(item1.ids));
      const matchIndex = candidates?.length ? candidates.shift() : -1;

      if (matchIndex !== -1) {

        matched.push({
          id: uuidV4(),
          value_date: item1.value_date || array2[matchIndex]?.value_date || "",
          description:
            item1.description || array2[matchIndex]?.description || "",
          amount: Number(item1[amountKey] || item1.ids || 0),
          source: sourceLabel,
          matched_with: matchedWithLabel,
          source_description: item1.description || "",
          matched_description: array2[matchIndex]?.description || "",
          source_date: item1.value_date || "",
          matched_date: array2[matchIndex]?.value_date || "",
        });
      }
    });

    return matched;
  }

  function getDifference(array1, array2) {
    const difference = [];
    const idCounts = {};

    for (const obj2 of array2) {
      const ids = obj2.ids;
      idCounts[ids] = (idCounts[ids] || 0) + 1;
    }

    for (const obj1 of array1) {
      const ids = obj1.ids;
      if (idCounts[ids] && idCounts[ids] > 0) {
        idCounts[ids]--;
      } else {
        difference.push(obj1);
      }
    }

    const difference2 = [];
    for (const obj2 of array2) {
      const ids = obj2.ids;
      if (idCounts[ids] && idCounts[ids] > 0) {
        difference2.push(obj2);
        idCounts[ids]--;
      }
    }

    return difference2;
  }

  const fetchPrevious = async () => {
    try {
      setLoader(true);
      const dates = [];
      const prevOutCr = await API.get(
        `/api/v1/outstanding-credit/previous-reconciliation/${location}`,
      );
      const prevOutDr = await API.get(
        `/api/v1/outstanding-debit/previous-reconciliation/${location}`,
      );
      if (prevOutCr.data[0]) {
        dates.push(new Date(prevOutCr.data[0].latestDate).toLocaleDateString());
      }
      if (prevOutDr.data[0]) {
        dates.push(new Date(prevOutDr.data[0].latestDate).toLocaleDateString());
      }
      const newDate = dates ? dates.map((d) => new Date(d)) : "";
      const maxDate = dates ? new Date(Math.max(...newDate)) : "";
      const finalDate = dates[0] ? moment(maxDate).format("YYYY-MM-DD") : "";

      if (finalDate) {
        const outCrPrev = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${location}&submission_date=${finalDate}`,
        );
        const outDrPrev = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${location}&submission_date=${finalDate}`,
        );
        setOutCrPrev(outCrPrev.data);
        setOutDrPrev(outDrPrev.data);
        setLoader(false);
      } else {
        setOutCrPrev([]);
        setOutDrPrev([]);
        setLoader(false);
      }
      setLoader(false);
    } catch (error) {
    } finally {
      setLoader(false);
    }
  };
  React.useEffect(() => {
    if (location && data.date) {
      fetchPrevious();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, data.date]);

  // Ids are only given once: a new id on every render changes the row keys and
  // makes React rebuild every row of every table, which froze big reconciliations
  outCrPrev1?.forEach((el) =>
    Object.assign(el, { ids: el.credit_amt, id: el.id || uuidV4() }),
  );
  outDrPrev1?.forEach((el) =>
    Object.assign(el, { ids: el.debit_amt, id: el.id || uuidV4() }),
  );

  const withPrev = React.useMemo(
    () => ({
      cr: [...cr_final, ...outCrPrev1],
      dr: [...dr_final, ...outDrPrev1],
    }),
    [cr_final, dr_final, outCrPrev1, outDrPrev1],
  );
  let cr_final_with_prev = withPrev.cr;
  let dr_final_with_prev = withPrev.dr;

  // Rows of each list grouped by amount, built once per list, so the tables
  // look up matches instead of scanning the whole list for every row
  const rowsByAmountCache = React.useRef(new WeakMap());
  function rowsWithAmount(arr, amountKey, amount) {
    let byAmount = rowsByAmountCache.current.get(arr);
    if (!byAmount) {
      byAmount = {};
      rowsByAmountCache.current.set(arr, byAmount);
    }
    if (!byAmount[amountKey]) {
      const groups = new Map();
      arr.forEach((item) => {
        const key = item[amountKey];
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });
      byAmount[amountKey] = groups;
    }
    return byAmount[amountKey].get(amount) || [];
  }

  function filterOutOutstanding(arr, outstanding) {
    let remaining = [
      ...rowsWithAmount(arr, "credit_amt", outstanding.credit_amt),
    ];

    const indexToRemove = remaining.findIndex(
      (item) =>
        item.credit_amt === outstanding.credit_amt &&
        item.description === outstanding.description &&
        item.value_date === outstanding.value_date,
    );

    if (indexToRemove !== -1) {
      remaining.splice(indexToRemove, 1);
    }

    return remaining;
  }
  function filterOutOutstandingDebit(arr, outstanding) {
    let remaining = [
      ...rowsWithAmount(arr, "debit_amt", outstanding.debit_amt),
    ];

    const indexToRemove = remaining.findIndex(
      (item) =>
        item.debit_amt === outstanding.debit_amt &&
        item.description === outstanding.description &&
        item.value_date === outstanding.value_date,
    );

    if (indexToRemove !== -1) {
      remaining.splice(indexToRemove, 1);
    }

    return remaining;
  }

  const reconcile = async (e) => {
    e.preventDefault();
    if (statementFinalData[0]) {
      if (location && data.date) {
        if (outCr.length > 0 || outDr.length > 0) {
          const confirm = window.confirm("Do you want to reconcile again?");
          if (!confirm) {
            message.info("operation aborted");
            return;
          }
        }

        const outstandingCredits = getDifference(
          dr_final_with_prev,
          cr_final_with_prev,
        );
        const outstandingDebits = getDifference(
          cr_final_with_prev,
          dr_final_with_prev,
        );

        const removedCreditsMatchedWithDebits = getMatchedItems(
          cr_final_with_prev,
          dr_final_with_prev,
          "credit_amt",
          "Credit",
          "Debit",
        );

        const removedDebitsMatchedWithCredits = getMatchedItems(
          dr_final_with_prev,
          cr_final_with_prev,
          "debit_amt",
          "Debit",
          "Credit",
        );

        const combinedRemoved = [
          ...removedCreditsMatchedWithDebits,
          ...removedDebitsMatchedWithCredits,
        ];

        const uniqueRemoved = uniqueObjects(combinedRemoved);

        setAutoRemovedItems(uniqueRemoved);
        setSelectedAutoRemovedRows([]);

        setOutCr(outstandingCredits);
        setOutDr(outstandingDebits);
        setCrPage(0);
        setDrPage(0);
        setRemovedPage(0);
        setTrigger(true);
      } else {
        toast.error("Please choose a location and date to reconcile");
      }
    } else {
      toast.error("Please select a statement file");
      setTrigger(false);
    }
  };

  outCr?.forEach((el) => {
    if (!el.id) el.id = uuidV4();
  });
  outDr?.forEach((el) => {
    if (!el.id) el.id = uuidV4();
  });

  const outstandingCredits = [];
  const outstandingDebits = [];

  outCr?.map((el) => outstandingCredits.push(el.credit_amt));
  outDr?.map((el) => outstandingDebits.push(el.debit_amt));

  const outCreditsTotal = outstandingCredits.reduce((acc, val) => acc + val, 0);
  const outDebitsTotal = outstandingDebits.reduce((acc, val) => acc + val, 0);

  const [balancePerStatement, setBalancePerStatement] = React.useState("");
  const [balancePerBooks, setBalancePerBooks] = React.useState("");
  const computedBookBalance =
    balancePerStatement * 1 + outCreditsTotal * 1 - outDebitsTotal * 1;

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
  });
  function ageing(date1, date2) {
    const formatDate1 = moment(date1).format("YYYY-MM-DD");
    const formatDate2 = moment(date2).format("YYYY-MM-DD");
    const diff = moment(formatDate2).diff(moment(formatDate1), "days");
    return diff;
  }

  const selected = true;
  const type = "debit account";

  const preparedOutDr = outDr?.map((el) =>
    Object.assign(el, {
      submission_date: data.date,
      branch: location,
      location: "primary",
      account_type: type,
      prepared_by: "",
    }),
  );
  const preparedOutCr = outCr?.map((el) =>
    Object.assign(el, {
      submission_date: data.date,
      branch: location,
      location: "primary",
      account_type: type,
      prepared_by: "",
    }),
  );
  const submissionData = {
    submission_date: data.date,
    branch: location,
    second_branch: location,
    account_type: type,
    secondary_balance: balancePerStatement,
    primary_balance: computedBookBalance,
    approved: "false",
    status: "new prepared reconciliation",
    prepared_by: user.results.firstName + " " + user.results.lastName,
  };
  const sendingCreditData = [...preparedOutCr];
  const sendingDebitData = [...preparedOutDr];
  const sendingCreditDataModified = sendingCreditData?.map(
    ({ _id, __v, balance, ...rest }) => rest,
  );
  const sendingDebitDataModified = sendingDebitData?.map(
    ({ _id, __v, balance, ...rest }) => rest,
  );

  // Multiple row selection and removing of selected rows here
  const [outCreditRows, setOutCreditRows] = React.useState([]);
  const [outDebitRows, setOutDebitRows] = React.useState([]);

  const getSelectedTotal = (rows = [], selectedIndexes = [], amountKey) => {
    return selectedIndexes.reduce((sum, index) => {
      const amount = Number(rows?.[index]?.[amountKey] || 0);
      return sum + amount;
    }, 0);
  };

  const checkedSummary = React.useMemo(() => {
    const totalCheckedCredit = getSelectedTotal(
      preparedOutCr,
      outCreditRows,
      "credit_amt",
    );

    const totalCheckedDebit = getSelectedTotal(
      preparedOutDr,
      outDebitRows,
      "debit_amt",
    );

    const difference = totalCheckedCredit - totalCheckedDebit;

    const totalCheckedRows = outCreditRows.length + outDebitRows.length;

    return {
      totalCheckedCredit,
      totalCheckedDebit,
      difference,
      totalCheckedRows,
    };
  }, [preparedOutCr, preparedOutDr, outCreditRows, outDebitRows]);

  const handleRowCheckboxChangeOutCr = (index) => {
    const selected = [...outCreditRows];
    if (selected.includes(index)) {
      setOutCreditRows(selected.filter((id) => id !== index));
    } else {
      setOutCreditRows([...selected, index]);
    }
  };
  const handleRowCheckboxChangeOutDr = (index) => {
    const selected = [...outDebitRows];
    if (selected.includes(index)) {
      setOutDebitRows(selected.filter((id) => id !== index));
    } else {
      setOutDebitRows([...selected, index]);
    }
  };

  const [deletedHistory, setDeletedHistory] = useState([]);
  const deleteSelectedRows = () => {
    const batch = [];

    outCreditRows.forEach((index) => {
      batch.push({
        type: "outCr",
        row: preparedOutCr[index],
        index,
      });
    });

    outDebitRows.forEach((index) => {
      batch.push({
        type: "outDr",
        row: preparedOutDr[index],
        index,
      });
    });

    if (batch.length === 0) return;

    setDeletedHistory((prev) => {
      const updated = [...prev, batch];
      return updated.slice(-10);
    });

    const outCrUpdatedData = preparedOutCr.filter(
      (row, index) => !outCreditRows.includes(index),
    );
    const outDrUpdatedData = preparedOutDr.filter(
      (row, index) => !outDebitRows.includes(index),
    );

    setOutCr(outCrUpdatedData);
    setOutDr(outDrUpdatedData);

    setOutCreditRows([]);
    setOutDebitRows([]);
  };

  const undoPreviousAction = () => {
    if (deletedHistory.length === 0) {
      message.info("There are no previous actions");
      return;
    }

    const lastBatch = deletedHistory[deletedHistory.length - 1];

    setDeletedHistory((prev) => prev.slice(0, -1));

    lastBatch.forEach((item) => {
      switch (item.type) {
        case "outCr":
          setOutCr((prev) => {
            const newArr = [...prev];
            newArr.splice(item.index, 0, item.row);
            return newArr;
          });
          break;
        case "outDr":
          setOutDr((prev) => {
            const newArr = [...prev];
            newArr.splice(item.index, 0, item.row);
            return newArr;
          });
          break;
        default:
          break;
      }
    });
  };

  const calculateTotalValueOutCr = () => {
    let totalValue = 0;
    outCreditRows.forEach((index) => {
      const row = preparedOutCr[index];
      totalValue += row.credit_amt;
    });
    return totalValue;
  };
  const calculateTotalValueOutDr = () => {
    let totalValue = 0;
    outDebitRows.forEach((index) => {
      const row = preparedOutDr[index];
      totalValue += row.debit_amt;
    });
    return totalValue;
  };

  const [expandingData, setExpandingData] = useState({});

  function handleExpandingData(el) {
    if (expandingData && expandingData === el) {
      setExpandingData({});
      return;
    }
    setExpandingData(el);
  }
  const rowRefs = React.useRef({});
  const lastFoundRowRef = React.useRef(null);

  // Statements can leave tens of thousands of outstanding rows, and drawing
  // them all at once freezes the browser, so the tables show one page at a time
  const ROWS_PER_PAGE_OPTIONS = [50, 100, 250, 500];
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [crPage, setCrPage] = useState(0);
  const [drPage, setDrPage] = useState(0);
  const [removedPage, setRemovedPage] = useState(0);
  const [pendingScrollRow, setPendingScrollRow] = useState(null);

  const lastPage = (count) => Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const crPageShown = Math.min(crPage, lastPage(outCr.length));
  const drPageShown = Math.min(drPage, lastPage(outDr.length));
  const removedPageShown = Math.min(
    removedPage,
    lastPage(sortedAutoRemovedItems.length),
  );
  const crStart = crPageShown * rowsPerPage;
  const drStart = drPageShown * rowsPerPage;
  const removedStart = removedPageShown * rowsPerPage;

  // Opens the page holding the row, then scrolls to it once it is drawn
  function showRow(tableKey, index) {
    const page = Math.floor(index / rowsPerPage);
    if (tableKey === "outCr") setCrPage(page);
    else setDrPage(page);
    setPendingScrollRow(`${tableKey}-${index}`);
  }

  React.useEffect(() => {
    if (!pendingScrollRow) return;
    rowRefs.current[pendingScrollRow]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    setPendingScrollRow(null);
  }, [pendingScrollRow, crPage, drPage]);

  function showMatchingRow(tableKey, amountKey, amount) {
    const rows = tableKey === "outCr" ? outCr : outDr;
    const index = rows.findIndex(
      (row) => Number(row[amountKey]) === Number(amount),
    );
    if (index !== -1) showRow(tableKey, index);
  }

  function renderPager(count, page, setPage) {
    if (count <= ROWS_PER_PAGE_OPTIONS[0]) return null;
    return (
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setCrPage(0);
          setDrPage(0);
          setRemovedPage(0);
        }}
      />
    );
  }

  function renderPagerRow(count, page, setPage) {
    const pager = renderPager(count, page, setPage);
    if (!pager) return null;
    return (
      <TableRow>
        <TableCell
          colSpan={7}
          style={{ position: "static", backgroundColor: "#fff", padding: 0 }}
        >
          {pager}
        </TableCell>
      </TableRow>
    );
  }
  const [highlightedDifferenceRow, setHighlightedDifferenceRow] =
    React.useState(null);

  const normalizeMoney = (value) => {
    const number = Number(value || 0);
    if (Number.isNaN(number)) return 0;
    return Math.round(number * 100) / 100;
  };

  const sameMoney = (a, b) => {
    return Math.abs(normalizeMoney(a) - normalizeMoney(b)) < 0.01;
  };

  const setDifferenceRowRef = (tableKey, index) => (node) => {
    if (node) {
      rowRefs.current[`${tableKey}-${index}`] = node;
    } else {
      delete rowRefs.current[`${tableKey}-${index}`];
    }
  };

  const getDifferenceHighlightStyle = (rowKey, el) => {
    const isFoundDifferenceRow = highlightedDifferenceRow === rowKey;

    return {
      backgroundColor: isFoundDifferenceRow
        ? "#fff7cc"
        : el === expandingData
          ? "#e2e8f0"
          : "",
      boxShadow: isFoundDifferenceRow ? "inset 0 0 0 2px #f59e0b" : "none",
      transition: "all 0.25s ease",
    };
  };

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const difference = normalizeMoney(checkedSummary.difference);

      if (checkedSummary.totalCheckedRows === 0 || sameMoney(difference, 0)) {
        setHighlightedDifferenceRow(null);
        lastFoundRowRef.current = null;
        return;
      }

      const targetAmount = Math.abs(difference);

      const debitTables = [
        {
          tableKey: "outDr",
          label: "OUTSTANDING DEBITS",
          rows: preparedOutDr,
          amountKey: "debit_amt",
          selectedRows: outDebitRows,
        },
      ];

      const creditTables = [
        {
          tableKey: "outCr",
          label: "OUTSTANDING CREDITS",
          rows: preparedOutCr,
          amountKey: "credit_amt",
          selectedRows: outCreditRows,
        },
      ];

      const tablesToSearch = difference > 0 ? debitTables : creditTables;

      let found = null;

      for (const table of tablesToSearch) {
        const foundIndex = table.rows?.findIndex((row, rowIndex) => {
          const rowIsAlreadyChecked = table.selectedRows.includes(rowIndex);

          return (
            !rowIsAlreadyChecked &&
            sameMoney(row?.[table.amountKey], targetAmount)
          );
        });

        if (foundIndex !== -1) {
          found = {
            tableKey: table.tableKey,
            label: table.label,
            index: foundIndex,
            amount: targetAmount,
          };
          break;
        }
      }

      if (!found) {
        setHighlightedDifferenceRow(null);
        lastFoundRowRef.current = null;
        return;
      }

      const rowKey = `${found.tableKey}-${found.index}`;
      setHighlightedDifferenceRow(rowKey);

      if (lastFoundRowRef.current !== rowKey) {
        showRow(found.tableKey, found.index);

        message.success(
          `Difference ${targetAmount.toLocaleString()} found in ${found.label}`,
        );

        lastFoundRowRef.current = rowKey;
      }
    }, 120);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkedSummary.difference,
    checkedSummary.totalCheckedRows,
    preparedOutCr,
    preparedOutDr,
    outCreditRows,
    outDebitRows,
  ]);

  function swapOutstandingCredit(main, swap) {
    const mainIndexInOutCr = outCr.findIndex(
      (d) =>
        d.credit_amt === main.credit_amt && d.description === main.description,
    );

    const swapIndexInCrFinalWithPrev = cr_final_with_prev.findIndex(
      (d) =>
        d.credit_amt === swap.credit_amt && d.description === swap.description,
    );

    if (mainIndexInOutCr !== -1 && swapIndexInCrFinalWithPrev !== -1) {
      const newOutCr = [...outCr];
      newOutCr[mainIndexInOutCr] = swap;

      const newCrFinalWithPrev = [...cr_final_with_prev];
      newCrFinalWithPrev[swapIndexInCrFinalWithPrev] = main;

      setOutCr(newOutCr);
      cr_final_with_prev = newCrFinalWithPrev;

      toast.success("Successfully swapped");
    } else {
      toast.error("Item not found for swapping");
    }
  }

  function swapOutstandingDebit(main, swap) {
    const mainIndexInOutDr = outDr.findIndex(
      (d) =>
        d.debit_amt === main.debit_amt && d.description === main.description,
    );

    const swapIndexInDrFinalWithPrev = dr_final_with_prev.findIndex(
      (d) =>
        d.debit_amt === swap.debit_amt && d.description === swap.description,
    );

    if (mainIndexInOutDr !== -1 && swapIndexInDrFinalWithPrev !== -1) {
      const newOutDr = [...outDr];
      newOutDr[mainIndexInOutDr] = swap;

      const newDrFinalWithPrev = [...dr_final_with_prev];
      newDrFinalWithPrev[swapIndexInDrFinalWithPrev] = main;

      setOutDr(newOutDr);
      dr_final_with_prev = newDrFinalWithPrev;

      toast.success("Successfully swapped");
    } else {
      toast.error("Item not found for swapping");
    }
  }

  const [fileName, setFileName] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);

        let workbook;
        if (file.name.endsWith(".csv")) {
          const text = new TextDecoder("utf-8").decode(data);
          workbook = XLSX.read(text, { type: "string" });
        } else {
          workbook = XLSX.read(data, { type: "array" });
        }

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          toast.error("No sheets found in workbook");
          return;
        }

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const allRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        if (!Array.isArray(allRows) || allRows.length === 0) {
          toast.error("No rows found in sheet");
          return;
        }

        const headerRowIndex = allRows.findIndex(
          (row) =>
            Array.isArray(row) &&
            row.some((cell) => {
              if (cell === null || cell === undefined) return false;
              return String(cell).toLowerCase().trim() === "value date";
            }),
        );

        if (headerRowIndex === -1) {
          toast.error("Could not find 'Value Date' column in this file.");
          return;
        }

        const trimmedRows = allRows.slice(headerRowIndex);

        if (trimmedRows.length <= 1) {
          toast.error("No data rows found after the header row.");
        }

        const headers = trimmedRows[0].map((h) =>
          String(h).toLowerCase().trim(),
        );
        const requiredHeaders = [
          "value date",
          "description",
          "debit amt",
          "credit amt",
        ];
        const missingHeaders = requiredHeaders.filter(
          (req) => !headers.includes(req),
        );

        if (missingHeaders.length > 0) {
          toast.error(
            `Missing required column(s): ${missingHeaders.join(", ")}`,
          );
          return;
        }

        const cleanSheet = XLSX.utils.aoa_to_sheet(trimmedRows);
        const jsonData = XLSX.utils.sheet_to_json(cleanSheet, {
          defval: "",
          raw: false,
        });

        const cleanedData = cleanData(jsonData);
        setFileName(file.name);
        setStatementData(cleanedData);
        message.info("Statement Data Loaded");
      } catch (err) {
        toast.error("There was an error processing the file.");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleRemoveFile = () => {
    setFileName(null);
    setStatementData([]);
    setTrigger(false);
    setAutoRemovedItems([]);
    setSelectedAutoRemovedRows([]);
    setRemovedItemsOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const cleanData = (data) => {
    const jsonObject = [];

    const headers = Object.keys(data[0] || {}).map((h) =>
      String(h).toLowerCase().trim().replace(/\s+/g, "_"),
    );

    const excelSerialToDate = (serial) => {
      if (serial === null || serial === undefined || serial === "") return "";
      if (typeof serial === "string") {
        if (/\d{1,2}-[A-Za-z]{3}-\d{2}/.test(serial)) return serial.trim();
        const num = parseFloat(serial);
        if (!isNaN(num)) serial = num;
        else return serial;
      }
      if (typeof serial !== "number") return serial;

      const excelEpoch = new Date(1899, 11, 30);
      const result = new Date(excelEpoch.getTime() + serial * 86400000);
      const day = result.getDate();
      const month = result.toLocaleString("en-GB", { month: "short" });
      const year = result.getFullYear().toString().slice(-2);
      return `${day}-${month}-${year}`;
    };

    const cleanNumber = (raw) => {
      if (raw === null || raw === undefined || raw === "") return 0;
      let s = String(raw).trim();
      s = s
        .replace(/^['"]+|['"]+$/g, "")
        .replace(/,/g, "")
        .replace(/-/g, "")
        .replace(/\s+/g, "");
      if (s === "" || s === ".") return 0;
      const n = parseFloat(s);
      return Number.isNaN(n) ? 0 : n;
    };

    data.forEach((row) => {
      const cleanedRow = {};

      headers.forEach((key) => {
        let value = row[key];

        if (value === undefined) {
          const headerIndex = headers.indexOf(key);
          if (headerIndex > -1) {
            value = Object.values(row)[headerIndex] ?? "";
          } else {
            value = "";
          }
        }

        if (key === "value_date" || key === "post_date") {
          value = excelSerialToDate(value);
        } else if (
          key === "debit_amt" ||
          key === "credit_amt" ||
          key === "balance"
        ) {
          value = cleanNumber(value);
        } else if (typeof value === "string") {
          value = value.replace(/,/g, " ").trim();
        }

        cleanedRow[key] = value;
      });

      jsonObject.push(cleanedRow);
    });

    let previousTransactionIndex = null;
    jsonObject.forEach((val, index) => {
      if (!val.value_date) {
        if (previousTransactionIndex !== null) {
          jsonObject[previousTransactionIndex].description =
            (jsonObject[previousTransactionIndex].description || "") +
            " " +
            (val.description || "");
        }
      } else {
        previousTransactionIndex = index;
      }
    });

    return jsonObject.filter(
      (row) => row.value_date && String(row.value_date).trim() !== "",
    );
  };

  // sorting outstanding
  outCr.sort((a, b) => b.credit_amt - a.credit_amt);
  outDr.sort((a, b) => b.debit_amt - a.debit_amt);

  // Amounts of each table, built once per table instead of scanned per row
  const amountSetCache = React.useRef(new WeakMap());
  function amountsOf(arr, amountKey) {
    let amounts = amountSetCache.current.get(arr);
    if (!amounts) {
      amounts = new Set(arr.map((item) => Number(item[amountKey])));
      amountSetCache.current.set(arr, amounts);
    }
    return amounts;
  }

  function existsInBothArrays(creditArray, debitArray, amount) {
    const foundInCredit = amountsOf(creditArray, "credit_amt").has(
      Number(amount),
    );

    const foundInDebit = amountsOf(debitArray, "debit_amt").has(Number(amount));

    return foundInCredit && foundInDebit ? "yes" : "";
  }

  const sendDataToServer = async () => {
    const { branch, submission_date, primary_balance } = submissionData || {};

    if (!branch || !submission_date || primary_balance == null) {
      toast.error(
        "Please make sure all these fields are filled: location, date, and computed balance.",
      );
      return;
    }

    setSubmitting(true);
    let loadingId;

    try {
      const payload = {
        credits: sendingCreditDataModified,
        debits: sendingDebitDataModified,
        submission: submissionData,
      };

      loadingId = toast.loading("submitting data...");
      await API.post("/api/v1/reconciliation-submission", payload);

      toast.remove(loadingId);
      toast.success("Reconciliation submitted successfully");

      setOutDr([]);
      setOutCr([]);
      setFileName(null);
      setTrigger(false);
      setOpen(false);
      setAutoRemovedItems([]);
      setSelectedAutoRemovedRows([]);
      setRemovedItemsOpen(false);

    } catch (err) {
      toast.remove(loadingId);
      const status = err.response?.status;

      if (status === 409) {
        toast.error(err.response?.data?.message || "Data already exists");
      } else {
        toast.error("Something went wrong while submitting data");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="scroll-container"
      style={{ paddingBottom: 40 }}
    >
      <Modal
        open={open}
        title="Please Confirm Submission"
        onCancel={handleCancel}
        footer={() => (
          <>
            <Button
              onClick={(e) => {
                e.preventDefault();
                sendDataToServer();
              }}
            >
              Submit
            </Button>
            <Button style={{ color: "red" }} onClick={handleCancel}>
              Cancel
            </Button>
          </>
        )}
      >
        <p>
          By submitting, you confirm that you have reviewed all items and
          acknowledge that they are outstanding.
        </p>
      </Modal>
      <Modal
        open={removedItemsOpen}
        title="System Auto Matched Items"
        onCancel={closeRemovedItemsModal}
        footer={() => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: 2,
              flexWrap: "wrap",
              zIndex: 200000000,
            }}
          >
            <Typography style={{ fontSize: "12px" }}>
              Selected: <strong>{selectedAutoRemovedRows.length}</strong>
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={restoreSelectedAutoRemovedItems}
                disabled={selectedAutoRemovedRows.length === 0}
              >
                Post Selected Back To Outstanding
              </Button>
              <Button onClick={closeRemovedItemsModal}>Close</Button>
            </Box>
          </Box>
        )}
        width={1050}
        zIndex={10000000}
      >
        {autoRemovedItems.length === 0 ? (
          <Typography>
            No auto matched items found for this reconciliation.
          </Typography>
        ) : (
          <>
            <Typography style={{ marginBottom: "10px", fontSize: "12px" }}>
              The records below are debit and credit entries within this
              statement that offset each other by amount and were
              automatically matched. They are sorted by amount. You can select
              any transaction(s) and post them back to the outstanding tables
              when the amounts are the same but the descriptions are
              different.
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      rowSpan={2}
                      style={{ fontWeight: 700, verticalAlign: "middle" }}
                    >
                      Select
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      style={{ fontWeight: 700, verticalAlign: "middle" }}
                    >
                      Grouped Amount
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      style={{ fontWeight: 700, verticalAlign: "middle" }}
                    >
                      Amount
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell style={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>
                      Description
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedAutoRemovedItems
                    .slice(removedStart, removedStart + rowsPerPage)
                    .map((item, i) => {
                      const index = removedStart + i;
                      const prevAmount =
                        index > 0
                          ? Number(sortedAutoRemovedItems[index - 1]?.amount || 0)
                          : null;

                      const currentAmount = Number(item.amount || 0);
                      const isNewAmountGroup =
                        index === 0 || prevAmount !== currentAmount;

                      const rowStyles = getTransactionTypeStyles(item.source);

                      return (
                        <React.Fragment key={item.id}>
                          {isNewAmountGroup && (
                            <TableRow>
                              <TableCell
                                colSpan={9}
                                style={{
                                  background: "#f5f5f5",
                                  fontWeight: 700,
                                  fontSize: "13px",
                                  color: "#262626",
                                  borderTop: "2px solid #d9d9d9",
                                }}
                              >
                                Amount Group:{" "}
                                <span style={getAmountGroupBadgeStyle()}>
                                  {currencyFormatter.format(currentAmount)}
                                </span>
                              </TableCell>
                            </TableRow>
                          )}

                          <TableRow
                            hover
                            style={{
                              ...rowStyles,
                              transition: "all 0.2s ease",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedAutoRemovedRows.includes(
                                  item.id,
                                )}
                                onChange={() =>
                                  toggleAutoRemovedSelection(item.id)
                                }
                              />
                            </TableCell>

                            <TableCell>
                              <span style={getAmountGroupBadgeStyle()}>
                                {currencyFormatter.format(currentAmount)}
                              </span>
                            </TableCell>

                            <TableCell
                              style={{ fontSize: "12px", fontWeight: 700 }}
                            >
                              {currencyFormatter.format(currentAmount)}
                            </TableCell>

                            <TableCell
                              style={{ fontSize: "12px", fontWeight: 600 }}
                            >
                              {item.source || "-"}
                            </TableCell>

                            <TableCell style={{ fontSize: "12px" }}>
                              {item.source_date
                                ? moment(item.source_date).format("DD-MM-YYYY")
                                : "-"}
                            </TableCell>

                            <TableCell
                              style={{ fontSize: "12px", fontWeight: 500 }}
                            >
                              {item.source_description || "-"}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            {renderPager(
              sortedAutoRemovedItems.length,
              removedPageShown,
              setRemovedPage,
            )}
          </>
        )}
      </Modal>
      <Helmet title="Single Statement Reconciliation | Reconciliation" />
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary }}>
          Reconcile a single statement
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: colors.textSecondary }}>
          Match the debits and credits within one location&apos;s statement
          and list what is still outstanding.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <FormControl sx={{ minWidth: 190 }} size="small">
          <InputLabel id="single-location-select-label">Location</InputLabel>
          <Select
            labelId="single-location-select-label"
            id="single-location-select"
            value={location}
            label="Location"
            onChange={handleChangeLocation}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {locations?.map((data) => (
              <MenuItem key={data._id} value={data.name}>
                {data.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="date"
          label="As at"
          InputLabelProps={{ shrink: true }}
          value={data.date}
          onChange={(e) => setData({ ...data, date: e.target.value })}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Tooltip title="Import the statement (.xlsx, .xls or .csv)">
            <Button
              variant={fileName ? "outlined" : "contained"}
              disableElevation
              size="small"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                ...(fileName
                  ? { color: colors.primary, borderColor: colors.border }
                  : { backgroundColor: colors.primary }),
              }}
            >
              {fileName ? "Change statement" : "Import statement"}
              <input
                hidden
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileUpload}
                ref={fileInputRef}
              />
            </Button>
          </Tooltip>
          <Typography
            noWrap
            title={fileName || ""}
            sx={{
              maxWidth: 260,
              fontSize: 13,
              color: fileName ? colors.textPrimary : colors.textSecondary,
            }}
          >
            {fileName || "No file chosen"}
          </Typography>
          {fileName && (
            <Button
              size="small"
              color="error"
              onClick={handleRemoveFile}
              sx={{ textTransform: "none", minWidth: 0 }}
            >
              Remove
            </Button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          {loader ? (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                zIndex: 9999,
              }}
            >
              <strong style={{ color: "#fff", marginBottom: "10px" }}>
                Bringing in outstanding items from the last reconciliation…
              </strong>
              <MutatingDots
                height="70"
                width="100"
                color="#F5AF14"
                secondaryColor="#1F354D"
                radius="12.5"
                ariaLabel="mutating-dots-loading"
                visible={true}
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <Button
                onClick={() => setRemovedItemsOpen(true)}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                }}
                disabled={autoRemovedItems.length === 0}
              >
                Review matched items ({autoRemovedItems.length})
              </Button>
              <Button
                onClick={reconcile}
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 3,
                  backgroundColor: colors.secondary,
                  "&:hover": { backgroundColor: colors.secondaryDark },
                }}
                variant="contained"
                size="small"
              >
                Reconcile
              </Button>
            </div>
          )}
        </div>
      </Box>
      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1200,
          maxWidth: "calc(100vw - 32px)",
          px: 1.5,
          py: 0.75,
          display: trigger ? "flex" : "none",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          backgroundColor: "rgba(255,255,255,0.96)",
          boxShadow: "0 12px 32px rgba(15, 23, 42, 0.18)",
        }}
      >
        <Tooltip title="Undo the last removal">
          <IconButton
            size="small"
            aria-label="Undo the last removal"
            onClick={undoPreviousAction}
          >
            <UndoIcon />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: 13 }}>
          <strong>Computed balance at period end: </strong>
          {balancePerStatement
            ? currencyFormatter.format(computedBookBalance)
            : "—"}
        </Typography>
        {(outCreditRows.length >= 1 || outDebitRows.length >= 1) && (
          <div>
            {(outCreditRows.length >= 1 || outDebitRows.length >= 1) && (
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"start"}
              >
                <Tooltip title="Take the ticked rows off the statement. Undo brings them back.">
                  <Button
                    onClick={deleteSelectedRows}
                    variant="contained"
                    color="error"
                    disableElevation
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
                  >
                    Remove ticked ({outCreditRows.length + outDebitRows.length})
                  </Button>
                </Tooltip>
              </Box>
            )}
          </div>
        )}
        <Tooltip
          title={
            computedBookBalance.toFixed(2) * 1 ===
            Number(balancePerBooks).toFixed(2) * 1
              ? ""
              : "The computed balance must equal the period-end balance (difference 0.00)"
          }
        >
          <span>
            <Button
              disabled={
                submitting ||
                computedBookBalance.toFixed(2) * 1 !==
                  Number(balancePerBooks).toFixed(2) * 1
              }
              onClick={showModal}
              size="small"
              variant="contained"
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 2.5,
                backgroundColor: colors.secondary,
                "&:hover": { backgroundColor: colors.secondaryDark },
              }}
            >
              Submit
            </Button>
          </span>
        </Tooltip>
        {Number(balancePerBooks) > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                fontSize: 13,
              }}
            >
              {computedBookBalance.toFixed(2) * 1 === balancePerBooks * 1 ? (
                <CheckBoxIcon sx={{ color: colors.secondary }} />
              ) : (
                <ErrorIcon sx={{ color: colors.danger }} />
              )}
            </Typography>
            <Typography sx={{ fontSize: 13, ml: 0.5 }}>
              Difference:{" "}
              {(Number(balancePerBooks) - Number(computedBookBalance)).toFixed(
                2,
              )}
            </Typography>
          </div>
        )}
      </Paper>
      {trigger ? (
        <Paper style={{ marginTop: "10px" }}>
          <TableContainer style={{ paddingBottom: "90px" }} component={Paper}>
            <Table
              sx={{
                tableLayout: "fixed",
                width: "100%",

                "& td, & th": {
                  padding: "6px 6px",
                  verticalAlign: "middle",
                },

                "& tr > *:nth-of-type(2)": {
                  whiteSpace: "nowrap",
                },

                "& tr > *:nth-of-type(3)": {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },

                "& tr > *:nth-of-type(4)": {
                  whiteSpace: "nowrap",
                  textAlign: "right",
                },

                "& tr > *:nth-of-type(5)": {
                  whiteSpace: "nowrap",
                  textAlign: "center",
                },

                "& tr > *:last-child": {
                  position: "sticky",
                  right: 0,
                  bgcolor: "#ccc",
                },
              }}
              size="small"
              aria-label="a dense table"
            >
              <colgroup>
                <col style={{ width: "52px" }} />
                <col style={{ width: "105px" }} />
                <col style={{ width: "360px" }} />
                <col style={{ width: "105px" }} />
                <col style={{ width: "70px" }} />
                <col style={{ width: "82px" }} />
                <col style={{ width: "82px" }} />
              </colgroup>

              <TableHead
                style={{ backgroundColor: "#fff", fontWeight: "bolder" }}
              >
                <TableRow style={{ fontWeight: "bolder" }}>
                  <TableCell
                    style={{
                      fontSize: "12px",
                      fontWeight: "bolder",
                      textTransform: "uppercase",
                    }}
                    align="center"
                    colSpan={7}
                  >
                    RECONCILIATION STATEMENT FOR{" "}
                    {location ? location : "[LOCATION NAME]"}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={3}
                    style={{ fontSize: "12px", fontWeight: "bold" }}
                  >
                    Balance At Period Start
                  </TableCell>

                  <TableCell colSpan={4} style={{ fontSize: "12px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "6px",
                        whiteSpace: "nowrap",
                        overflow: "visible",
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        value={balancePerStatement}
                        onChange={(e) =>
                          setBalancePerStatement(e.target.value)
                        }
                        placeholder="Statement balance"
                        inputProps={{ "aria-label": "Balance at period start" }}
                        sx={{ width: 170, "& input": { py: 0.6, fontSize: 13 } }}
                      />
                    </div>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    colSpan={3}
                    style={{ fontSize: "12px", fontWeight: "bold" }}
                  >
                    Balance At Period End
                  </TableCell>

                  <TableCell colSpan={4} style={{ fontSize: "12px" }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Your book balance"
                      inputProps={{ "aria-label": "Balance at period end" }}
                      sx={{ width: 170, "& input": { py: 0.6, fontSize: 13 } }}
                      value={balancePerBooks}
                      onChange={(e) => setBalancePerBooks(e.target.value)}
                    />
                  </TableCell>
                </TableRow>

                <TableRow style={{ backgroundColor: "#F5AF14" }}>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    SELECT
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    DATE
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    DESCRIPTION
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    OUTSTANDING
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    AGING
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    ACTION
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    CHECK
                  </TableCell>
                </TableRow>

                {/* OUTSTANDING CREDIT */}
                <TableRow style={{ backgroundColor: "#ccc" }}>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      "ADD: OUTSTANDING CREDIT"
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>
                    <a href="#debits">
                      <Button
                        style={{ color: "red", fontSize: "11px" }}
                        variant="outlined"
                        size="small"
                      >
                        DEBITS
                      </Button>
                    </a>
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {outCr?.slice(crStart, crStart + rowsPerPage).map((el, i) => {
                  const index = crStart + i;
                  const rowKey = `outCr-${index}`;

                  return (
                    <React.Fragment key={el.id || rowKey}>
                      <TableRow
                        ref={setDifferenceRowRef("outCr", index)}
                        id="credits"
                        style={getDifferenceHighlightStyle(rowKey, el)}
                        className={
                          outCreditRows.includes(index) ? "selected" : ""
                        }
                      >
                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            <Checkbox
                              size="small"
                              checked={outCreditRows.includes(index)}
                              onChange={() =>
                                handleRowCheckboxChangeOutCr(index)
                              }
                            />
                          ) : (
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            moment(el.value_date).format("DD-MM-YYYY")
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            el.description
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell
                          id={`credit${el.credit_amt}`}
                          style={{ fontSize: "12px" }}
                        >
                          {selected ? (
                            currencyFormatter.format(el.credit_amt)
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            data.date ? (
                              ageing(el.value_date, data.date)
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        {filterOutOutstanding(cr_final_with_prev, el).length >
                        0 ? (
                          <TableCell style={{ fontSize: "12px" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandingData(el)}
                            >
                              {el === expandingData ? (
                                <ArrowDropUpIcon />
                              ) : (
                                <ArrowDropDownIcon />
                              )}
                            </IconButton>

                            <Tooltip title="matched items">
                              <IconButton size="small">
                                <Typography fontSize="12px" color="red">
                                  {
                                    filterOutOutstanding(
                                      cr_final_with_prev,
                                      el,
                                    ).length
                                  }
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell></TableCell>
                        )}

                        <TableCell style={{ cursor: "pointer" }}>
                          <a
                            href={`#debit${el.credit_amt}`}
                            onClick={(e) => {
                              e.preventDefault();
                              showMatchingRow(
                                "outDr",
                                "debit_amt",
                                el.credit_amt,
                              );
                            }}
                            style={{ textDecoration: "none" }}
                          >
                            {existsInBothArrays(outCr, outDr, el.credit_amt)}
                          </a>
                        </TableCell>
                      </TableRow>

                      {el === expandingData && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>CREDITS</strong> The following credits
                              reconciled with the debits below{" "}
                              <ArrowDownwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {filterOutOutstanding(cr_final_with_prev, el).map(
                            (sub, subIndex) => (
                              <TableRow
                                key={`cr-sub-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#fdf2f8" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.credit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  <Tooltip title="swap with main outstanding">
                                    <IconButton
                                      onClick={() =>
                                        swapOutstandingCredit(el, sub)
                                      }
                                      size="small"
                                    >
                                      <SwapVertIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ),
                          )}

                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>DEBITS</strong> | The following debits
                              reconciled with the credits above{" "}
                              <ArrowUpwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {dr_final_with_prev
                            .filter(
                              (sub) =>
                                Number(sub.debit_amt) === Number(el.credit_amt),
                            )
                            .map((sub, subIndex) => (
                              <TableRow
                                key={`dr-match-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#e7e5e4" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.debit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                              </TableRow>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {renderPagerRow(outCr.length, crPageShown, setCrPage)}

                <TableRow>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    Total
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {calculateTotalValueOutCr() > 0 &&
                      currencyFormatter.format(calculateTotalValueOutCr())}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      currencyFormatter.format(outCreditsTotal)
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {/* OUTSTANDING DEBIT */}
                <TableRow style={{ backgroundColor: "#ccc" }}>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      "LESS: OUTSTANDING DEBIT"
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>
                    <a href="#credits">
                      <Button
                        variant="outlined"
                        style={{ fontSize: "10px" }}
                        size="small"
                      >
                        CREDITS
                      </Button>
                    </a>
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {outDr?.slice(drStart, drStart + rowsPerPage).map((el, i) => {
                  const index = drStart + i;
                  const rowKey = `outDr-${index}`;

                  return (
                    <React.Fragment key={el.id || rowKey}>
                      <TableRow
                        ref={setDifferenceRowRef("outDr", index)}
                        id="debits"
                        style={getDifferenceHighlightStyle(rowKey, el)}
                        className={
                          outDebitRows.includes(index) ? "selected" : ""
                        }
                      >
                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            <Checkbox
                              size="small"
                              checked={outDebitRows.includes(index)}
                              onChange={() =>
                                handleRowCheckboxChangeOutDr(index)
                              }
                            />
                          ) : (
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            moment(el.value_date).format("DD-MM-YYYY")
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            el.description
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell
                          id={`debit${el.debit_amt}`}
                          style={{ fontSize: "12px" }}
                        >
                          {selected ? (
                            currencyFormatter.format(el.debit_amt)
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            data.date ? (
                              ageing(el.value_date, data.date)
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        {filterOutOutstandingDebit(dr_final_with_prev, el)
                          .length > 0 ? (
                          <TableCell style={{ fontSize: "12px" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandingData(el)}
                            >
                              {el === expandingData ? (
                                <ArrowDropUpIcon />
                              ) : (
                                <ArrowDropDownIcon />
                              )}
                            </IconButton>

                            <Tooltip title="matched items">
                              <IconButton size="small">
                                <Typography fontSize="12px" color="red">
                                  {
                                    filterOutOutstandingDebit(
                                      dr_final_with_prev,
                                      el,
                                    ).length
                                  }
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell></TableCell>
                        )}

                        <TableCell style={{ cursor: "pointer" }}>
                          <a
                            href={`#credit${el.debit_amt}`}
                            onClick={(e) => {
                              e.preventDefault();
                              showMatchingRow(
                                "outCr",
                                "credit_amt",
                                el.debit_amt,
                              );
                            }}
                            style={{ textDecoration: "none" }}
                          >
                            {existsInBothArrays(outCr, outDr, el.debit_amt)}
                          </a>
                        </TableCell>
                      </TableRow>

                      {el === expandingData && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>DEBITS</strong> The following debits
                              reconciled with the credits below{" "}
                              <ArrowDownwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {filterOutOutstandingDebit(
                            dr_final_with_prev,
                            el,
                          ).map((sub, subIndex) => (
                            <TableRow
                              key={`dr-sub-${subIndex}`}
                              className="element"
                              style={{ backgroundColor: "#fdf2f8" }}
                            >
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {moment(sub.value_date).format("DD-MM-YYYY")}
                              </TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {sub.description}
                              </TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {currencyFormatter.format(sub.debit_amt)}
                              </TableCell>
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                <Tooltip title="swap with main outstanding">
                                  <IconButton
                                    onClick={() =>
                                      swapOutstandingDebit(el, sub)
                                    }
                                    size="small"
                                  >
                                    <SwapVertIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}

                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>CREDITS</strong> | The following credits
                              reconciled with the debits above{" "}
                              <ArrowUpwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {cr_final_with_prev
                            .filter(
                              (sub) =>
                                Number(sub.credit_amt) === Number(el.debit_amt),
                            )
                            .map((sub, subIndex) => (
                              <TableRow
                                key={`cr-match-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#e7e5e4" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.credit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                              </TableRow>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {renderPagerRow(outDr.length, drPageShown, setDrPage)}

                <TableRow>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    Total
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {calculateTotalValueOutDr() > 0 &&
                      currencyFormatter.format(calculateTotalValueOutDr())}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      currencyFormatter.format(outDebitsTotal)
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>---------</TableCell>
                  <TableCell style={{ fontSize: "12px" }}>---------</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            minHeight: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            borderRadius: "18px",
            border: "1px dashed #cbd5df",
            background: colors.surface,
          }}
        >
          <Box sx={{ maxWidth: 520 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
              {statementData[0] ? "The statement is loaded" : "Get started"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: colors.textSecondary, mb: 2 }}>
              {statementData[0]
                ? "Click Reconcile to match it. Outstanding items from the last reconciliation are brought in automatically."
                : "Follow these steps to reconcile one statement."}
            </Typography>
            <Stack spacing={1.2}>
              <StepLine done={Boolean(location && data.date)} n={1}>
                Choose the location and the &quot;as at&quot; date.
              </StepLine>
              <StepLine done={Boolean(statementData[0])} n={2}>
                Import the statement.
              </StepLine>
              <StepLine done={false} n={3}>
                Click <strong>Reconcile</strong>, enter both balances, then{" "}
                <strong>Submit</strong> for approval.
              </StepLine>
            </Stack>
          </Box>
        </Paper>
      )}

      {checkedSummary.totalCheckedRows > 0 && (
        <Paper
          elevation={10}
          sx={{
            position: "fixed",
            right: 16,
            bottom: 90,
            zIndex: 1250,
            width: 240,
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid #d7e2ec",
            background: "#ffffff",
            boxShadow: "0 14px 35px rgba(15, 23, 42, 0.22)",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.2,
              background: "linear-gradient(135deg, #1F354D 0%, #33597c 100%)",
              color: "#fff",
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: "13px" }}>
              Checked Transactions
            </Typography>

            <Typography sx={{ fontSize: "10px", opacity: 0.85 }}>
              {checkedSummary.totalCheckedRows} selected row
              {checkedSummary.totalCheckedRows > 1 ? "s" : ""}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                Checked Credit
              </Typography>

              <Typography
                sx={{ fontSize: "12px", fontWeight: 800, color: "#15803d" }}
              >
                {currencyFormatter.format(checkedSummary.totalCheckedCredit)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                Checked Debit
              </Typography>

              <Typography
                sx={{ fontSize: "12px", fontWeight: 800, color: "#b91c1c" }}
              >
                {currencyFormatter.format(checkedSummary.totalCheckedDebit)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
                Difference
              </Typography>

              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 900,
                  color:
                    checkedSummary.difference === 0
                      ? "#15803d"
                      : checkedSummary.difference > 0
                        ? "#1d4ed8"
                        : "#b91c1c",
                }}
              >
                {currencyFormatter.format(checkedSummary.difference)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </div>
  );
}
